import React, { useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Row, 
  Col, 
  Card, 
  Typography, 
  Avatar, 
  Button, 
  message,
  Skeleton,
  Affix,
  Tag,
  Modal,
  Descriptions
} from 'antd';
import { 
  GlobalOutlined, 
  PhoneOutlined, 
  UserOutlined, 
  EnvironmentOutlined,
  InfoCircleOutlined,
  AppstoreOutlined,
  FileOutlined,
  BankOutlined,
  TeamOutlined,
  HomeOutlined,
  DollarCircleOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  ReadOutlined,
  AppstoreAddOutlined
} from '@ant-design/icons';
import company from '../../services/company';
import './index.css';
import SearchBar from '../../components/SearchBar';
import AppHeader from '../../components/Header';
import analytic from '../../services/analytic';
import env from '../../env';
import { SEARCH_STATE_KEY, SEARCH_LOB_KEY, SEARCH_TYPE_KEY } from './../../constants/searchConstants';

const { Title, Paragraph, Text, Link } = Typography;

const CompanyDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const startTime = useRef(Date.now());
  const maxScroll = useRef(0);

  // Fetch company details using React Query
  const { data: companyData, isLoading, error } = useQuery({
    queryKey: ['company', id],
    queryFn: async () => {
      const response = await company.getById(id);
      console.log(response);
      return response.data;
    },
  });

  // Track analytics mutation
  const trackAnalyticsMutation = useMutation({
    mutationFn: (data) => analytic.trackTime(data),
  });

  // Scroll handler
  const handleScroll = () => {
    const scrolled = window.scrollY + window.innerHeight;
    const totalHeight = document.documentElement.scrollHeight;
    const scrollPercentage = (scrolled / totalHeight) * 100;
    maxScroll.current = Math.max(maxScroll.current, scrollPercentage);
  };

  // Setup scroll tracking
  React.useEffect(() => {
    window.scrollTo(0, 0);
    window.addEventListener('scroll', handleScroll);
    startTime.current = Date.now();
    maxScroll.current = 0;

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (companyData?.id) {
        const timeSpent = Math.round((Date.now() - startTime.current) / 1000);
        trackAnalyticsMutation.mutate({
          company_id: companyData.id,
          time_spent: timeSpent,
          scroll_position: maxScroll.current.toFixed(2)
        });
      }
    };
  }, [companyData?.id]);

  // Update the handleSearch function
  const handleSearch = ({ state, lineOfBusiness, searchType }) => {
    // Save search state to localStorage
    if (state) localStorage.setItem(SEARCH_STATE_KEY, state);
    if (lineOfBusiness) localStorage.setItem(SEARCH_LOB_KEY, lineOfBusiness);
    if (searchType) localStorage.setItem(SEARCH_TYPE_KEY, 'lob');

    const params = new URLSearchParams();
    if (state) params.set('state', state);
    if (lineOfBusiness) params.set('line_of_business', lineOfBusiness);
    params.set('searchByLob', 'true');
    params.delete('searchByAo');

    navigate(`/?${params.toString()}`);
  };

  // Show error message if query fails
  if (error) {
    message.error('Failed to fetch company details');
  }

  const CompanyDetailsSkeleton = () => (
    <>
      <Card className="mobile-screen">
        <Row gutter={[24, 24]} align="middle">
          <Col xs={24} sm={8} md={12} lg={5}>
            <Skeleton.Avatar active size={200} shape="circle" />
          </Col>
          <Col xs={24} sm={16} md={12} lg={19}>
            <Skeleton.Input active size="large" block style={{ marginBottom: '0px' }} />
            <Skeleton active paragraph={{ rows: 1 }}  />
            <Row gutter={16} justify={{ xs: "center", sm: "start" }} align="middle">
  <Col>
    <Skeleton.Button active size="large" style={{marginTop: 30, width: 160}} />
  </Col>
  <Col>
    <Skeleton.Button active size="large" style={{marginTop: 30, width: 160}} />
  </Col>
</Row>

          </Col>
        </Row>
      </Card>

      <Row gutter={[24, 24]} className="company-content" style={{marginTop: 10}}>
        <Col xs={24} md={16}>
          <Card>
            <Skeleton active paragraph={{ rows: 6 }} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Skeleton active paragraph={{ rows: 4 }} />
          </Card>
        </Col>
      </Row>
    </>
  );

  const InfoSection = ({ icon, label, values, isLink, isTag }) => (
    <div className="info-section">
      <span className="info-icon">{icon}</span>
      <div className="info-content">
        <div className="info-label">{label}</div>
        {isTag ? (
          <div className="info-tags">
            {values.map((value, index) => (
              <Tag 
                key={index} 
                color={label.includes('State') ? 'blue' : 'green'}
              >
                {value}
              </Tag>
            ))}
          </div>
        ) : isLink ? (
          <a href={values[0]} target="_blank" rel="noopener noreferrer" className="info-value link">
            {values[0]}
          </a>
        ) : (
          <div className="info-value">{values[0]}</div>
        )}
      </div>
    </div>
  );

  // Add state for job modal
  const [selectedJob, setSelectedJob] = React.useState(null);
  const [isJobModalVisible, setIsJobModalVisible] = React.useState(false);

  // Add handlers for job modal
  const showJobModal = (job) => {
    setSelectedJob(job);
    setIsJobModalVisible(true);
  };

  const handleJobModalClose = () => {
    setIsJobModalVisible(false);
    setSelectedJob(null);
  };

  // Update the handleBackToSearch function
  const handleBackToSearch = () => {
    // Get current search type and parameters
    const state = searchParams.get('state');
    const lineOfBusiness = searchParams.get('line_of_business');

    if (state) localStorage.setItem(SEARCH_STATE_KEY, state);
    if (lineOfBusiness) localStorage.setItem(SEARCH_LOB_KEY, lineOfBusiness);
    localStorage.setItem(SEARCH_TYPE_KEY, 'lob');

    const params = new URLSearchParams(searchParams);
    params.delete('searchByAo');
    if (state || lineOfBusiness) {
      params.set('searchByLob', 'true');
    }

    navigate(`/?${params.toString()}`);
  };

  return (
    <>
      <AppHeader />
      <div className="company-details-container">
        <SearchBar 
          onSearch={handleSearch} 
          showTitle={false}
          showQuickAccess={false}
        />
        
        {isLoading ? (
          <div className="details-wrapper">
            <CompanyDetailsSkeleton />
          </div>
        ) : companyData ? (
          <div className="details-wrapper">
            <Card className={companyData.sponsored === "1" ? 'sponsored-card' : ''}>
              <Row gutter={[24, 24]} align="middle" className='mobile-screen'>
                <Col xs={24} sm={8} md={6} lg={5} >
                  <div className="logo-container">
                   
                    {companyData.sponsored === "1" ? (
                      <Tag color="#2196F3" className="sponsored-tag">
                        SPONSORED
                      </Tag>
                    ) : ''}
                  </div>
                </Col>
                <Col xs={24} sm={16} md={18} lg={19}>
                  <div className="">
                    <Title level={2} className="company-title">{companyData.name}</Title>
                   
                    <div className="">
                      {companyData.address && (
                        <div style={{ marginTop: '10px' }}>
                          <EnvironmentOutlined />
                          <Text>{companyData.address}</Text>
                        </div>
                      )}
                      {companyData.phone && (
                        <div style={{ marginTop: '8px' }}>
                          <PhoneOutlined />
                          <Text>{companyData.phone}</Text>
                        </div>
                      )}
                    </div>
                    <Row gutter={16} className="quick-actions">
                      {companyData.website && (
                        <Col>
                          <Button 
                            type="primary" 
                            icon={<GlobalOutlined />}
                            href={companyData.website}
                            target="_blank"
                            size="large"
                            className="action-button"
                          >
                            Visit Website
                          </Button>
                        </Col>
                      )}
                      {companyData.phone && (
                        <Col>
                          <Button 
                            icon={<PhoneOutlined />}
                            href={`tel:${companyData.phone}`}
                            size="large"
                            className="action-button"
                          >
                            Call Now
                          </Button>
                        </Col>
                      )}
                    </Row>
                  </div>
                </Col>
              </Row>
            </Card>

            <Row gutter={[24, 24]} className="company-content" style={{marginTop: 10}}>
              <Col xs={24} md={16}>
                <Card 
                  title={<div className="section-title">Company Information</div>}
                  className="info-card"
                >
                  <div className="info-grid">
                    {companyData.contact && (
                      <InfoSection 
                        icon={<UserOutlined />}
                        label="Contact Person"
                        values={[companyData.contact]}
                      />
                    )}
                    {companyData.phone && (
                      <InfoSection 
                        icon={<PhoneOutlined />}
                        label="Phone"
                        values={[companyData.phone]}
                        isLink={true}
                      />
                    )}
                    {companyData.website && (
                      <InfoSection 
                        icon={<GlobalOutlined />}
                        label="Website"
                        values={[companyData.website]}
                        isLink={true}
                      />
                    )}
                    {companyData.address && (
                      <InfoSection 
                        icon={<EnvironmentOutlined />}
                        label="Address"
                        values={[companyData.address]}
                      />
                    )}
                    {companyData.states && companyData.states.length > 0 && (
                      <InfoSection 
                        icon={<BankOutlined />}
                        label="States"
                        values={companyData.states.map(state => state.name)}
                        isTag={true}
                      />
                    )}
                    {companyData.line_of_businesses && companyData.line_of_businesses.length > 0 && (
                      <InfoSection 
                        icon={<AppstoreOutlined />}
                        label="Line of Business"
                        values={companyData.line_of_businesses.map(business => business.name)}
                        isTag={true}
                      />
                    )}
                    {companyData.commission && (
                      <InfoSection 
                        icon={<InfoCircleOutlined />}
                        label="Commission"
                        values={[companyData.commission]}
                      />
                    )}
                    
                  </div>

                  {companyData.limits_of_coverage && (
                    <div className="general-info-section">
                      <Title level={4} className="section-subtitle">
                        Limits of Coverage
                      </Title>
                      <Paragraph className="general-info">
                        <div dangerouslySetInnerHTML={{ __html: companyData.limits_of_coverage }} />
                      </Paragraph>
                    </div>
                  )}

                  {companyData.underwriting && (
                    <div className="general-info-section">
                      <Title level={4} className="section-subtitle">
                        Underwriting
                      </Title>
                      <Paragraph className="general-info">
                        <div dangerouslySetInnerHTML={{ __html: companyData.underwriting }} />
                      </Paragraph>
                    </div>
                  )}

                  {companyData.general_info && (
                    <div className="general-info-section">
                      <Title level={4} className="section-subtitle">
                        About the Company
                      </Title>
                      <p className="general-info">
                        <div dangerouslySetInnerHTML={{ __html: companyData.general_info }} />
                      </p>
                    </div>
                  )}
                </Card>
              </Col>

              <Col xs={24} md={8}>

                {companyData.files && companyData.files.length > 0 && (
                  <Card 
                    title={
                      <div className="section-title">
                        <FileOutlined /> Company Documents
                      </div>
                    }
                    className="documents-card"
                  >
                    <div className="documents-list">
                      {companyData.files.map((file, index) => (
                        <Button
                          key={file.id || index}
                          type="text"
                          icon={<FileOutlined />}
                          href={`${env.BACKEND_STORAGE_URL}/${file.file_path}`}
                          target="_blank"
                          block
                          className="file-link"
                        >
                          <span className="file-name">{file.file_name || `Document ${index + 1}`}</span>
                          <div className="file-action">View</div>
                        </Button>
                      ))}
                    </div>
                  </Card>
                )}

                {companyData.jobs && companyData.jobs.length > 0 && (
                  <Card 
                    title={
                      <div className="section-title">
                        <TeamOutlined /> Open Positions
                      </div>
                    }
                    className="jobs-card"
                    style={{ marginTop: companyData.jobs?.length ? '14px' : 0 }}
                  >
                    <div className="jobs-list">
                      {companyData.jobs.map((job) => (
                        <div
                          key={job.id}
                          type="text"
                          block
                          onClick={() => showJobModal(job)}
                          className="job-link"
                        >
                          <div className="job-item">
                          <span className="job-title">
  {job.title.charAt(0).toUpperCase() + job.title.slice(1)}
</span>
                            <Tag color="blue">{job.job_type.name}</Tag>
                          </div>
                          <div className="job-location">
                            <EnvironmentOutlined /> {job.location}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </Col>
            </Row>
          </div>
        ) : null}
      </div>

      {/* Add Job Details Modal */}
      <Modal
        title={selectedJob?.title}
        open={isJobModalVisible}
        onCancel={handleJobModalClose}
        footer={[
          <Button key="close" onClick={handleJobModalClose}>
            Close
          </Button>,
          selectedJob?.apply_url && (
            <Button
              key="apply"
              type="primary"
              href={selectedJob.apply_url}
              target="_blank"
            >
              Apply Now
            </Button>
          ),
        ]}
        width={800}
      >
      
{selectedJob && (
  <div className="job-details">
    <div className="job-meta">
      <Descriptions bordered column={1}>
        <Descriptions.Item label={<><HomeOutlined /> Location:</>}>
          {selectedJob.location}
        </Descriptions.Item>
        <Descriptions.Item label={<><FileTextOutlined />  Type:</>}>
          {selectedJob.job_type.name}
        </Descriptions.Item>
        <Descriptions.Item label={<><DollarCircleOutlined /> Salary:</>}>
          {selectedJob.salary_range}
        </Descriptions.Item>
        <Descriptions.Item label={<><CheckCircleOutlined /> Status:</>}>
          {selectedJob.is_active ? 'Active' : 'Inactive'}
        </Descriptions.Item>
        {selectedJob.salary && (
          <Descriptions.Item label={<><DollarCircleOutlined /> Salary:</>}>
            {selectedJob.salary}
          </Descriptions.Item>
        )}
      </Descriptions>
    </div>

    <div className="job-description">
      <h4>Job Description</h4>
      <div dangerouslySetInnerHTML={{ __html: selectedJob.description }} />
    </div>

    {selectedJob.requirements && (
      <div className="job-requirements">
        <h4>Requirements</h4>
        <div dangerouslySetInnerHTML={{ __html: selectedJob.requirements }} />
      </div>
    )}
  </div>
)}
      </Modal>

      {/* Add this button or link somewhere in your UI */}
      <Button onClick={handleBackToSearch}>
        Back to Search Results
      </Button>
    </>
  );
};

export default CompanyDetailsPage;